import { Input, type InputChangeEvent } from '@progress/kendo-react-inputs'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/applicationAdmin/routes/paths'
import { HStack } from '~/components/elements/HStack'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'

import { useAiUsageQuery } from './hooks/useAiUsageQuery'
import { currentMonth, downloadCsv, mergeBuckets, toCsv } from './utils'

// The month must be `YYYY-MM` before it is safe to query and to build the
// filename. Guards against a cleared/partial native month input.
const isValidMonth = (m: string): boolean => /^\d{4}-\d{2}$/.test(m)

export const AiUsageExportPage = () => {
  const { t } = useTranslation()
  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs: PageContainerProps['breadcrumbs'] = React.useMemo(
    () => [createBreadcrumb('top', {}), createBreadcrumb('aiUsageExport', {})],
    [createBreadcrumb],
  )

  const notify = useNotify()
  const logEvent = useLogEvent()
  const fetchAiUsage = useAiUsageQuery()

  const [month, setMonth] = React.useState<string>(currentMonth())

  const handleChangeMonth = React.useCallback((event: InputChangeEvent) => {
    setMonth(event.value ?? '')
  }, [])

  const handleDownloadCsv = React.useCallback<
    NonNullable<StyledButtonProps['onClick']>
  >(async () => {
    try {
      const { analyzerAiUsage, brandAccounts, overallAiUsage } =
        await fetchAiUsage(month)

      const buckets = mergeBuckets([...analyzerAiUsage, ...overallAiUsage])
      const brandAccountNameById = new Map(
        brandAccounts.map((account) => [account.id, account.name]),
      )

      downloadCsv(toCsv(buckets, brandAccountNameById), `ai-usage-${month}.csv`)

      logEvent('Download AI usage', {
        eventProperties: [{ key: 'month', value: month }],
      })
    } catch {
      notify({
        content: t('csv_download_failed'),
        style: 'error',
      })
    }
  }, [fetchAiUsage, logEvent, month, notify, t])

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <HStack align="center" spacing={2}>
        <Input type="month" value={month} onChange={handleChangeMonth} />
        <StyledButton
          disabled={!isValidMonth(month)}
          icon="download"
          onClick={handleDownloadCsv}
        >
          {t('download_csv')}
        </StyledButton>
      </HStack>
    </PageContainer>
  )
}
